// BooLume product page elements.
import { formatMoney } from 'bl-offers';
import { getConfig } from 'bl-config';
import { restartClass } from 'bl-motion';
import { prefersReducedMotion } from 'bl-util';

class BlBundlePicker extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.form);
    const stock = Number(this.dataset.stock || 999);
    this.querySelectorAll('input[type=radio]').forEach((i) => {
      if (Number(i.dataset.qty) > stock) {
        i.disabled = true;
        i.checked = false;
      }
    });
    this.addEventListener('change', (e) => {
      if (e.target.matches('input[type=radio]')) this.apply(e.target, true);
    });
    const start = this.querySelector('input:checked:not(:disabled)') || this.querySelector('input:not(:disabled)');
    if (start) {
      start.checked = true;
      this.apply(start, false);
    }
  }
  apply(input, animate) {
    if (!this.form) return;
    this.form.querySelector('[name=id]').value = input.dataset.variant;
    this.form.querySelector('[name=quantity]').value = input.dataset.qty;
    const priceCents = Number(input.dataset.price);
    const text = formatMoney(priceCents, getConfig().moneyFormat);
    document.querySelectorAll(`[data-bl-price-for="${this.dataset.form}"]`).forEach((el) => (el.textContent = text));
    if (animate) restartClass(input.closest('.bl-tier'), 'is-pop');
    this.dispatchEvent(new CustomEvent('bl:offer-change', {
      bubbles: true,
      detail: { variantId: Number(input.dataset.variant), qty: Number(input.dataset.qty), priceCents, label: input.dataset.label },
    }));
  }
}
if (!customElements.get('bl-bundle-picker')) customElements.define('bl-bundle-picker', BlBundlePicker);

// A horizontal scroll-snap track: which slide is showing, and how to go to one.
function trackIndex(track) {
  return Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
}
function goTo(track, i) {
  const n = track.children.length;
  const to = (i + n) % n;
  track.scrollTo({ left: to * track.clientWidth, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
}
function onSettle(track, fn) {
  let queued = false;
  track.addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; fn(trackIndex(track)); });
  }, { passive: true });
}

class BlGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bl-gallery__track');
    this.total = this.track.children.length;
    this.current = this.querySelector('.bl-gallery__count [data-current]');
    this.pickers = [...this.querySelectorAll('.bl-gallery__dots button, .bl-gallery__thumbs button')];
    this.pickers.forEach((b) => b.addEventListener('click', () => goTo(this.track, Number(b.dataset.index))));
    this.querySelectorAll('.bl-gallery__main [data-step]').forEach((b) => b.addEventListener('click', () => {
      goTo(this.track, trackIndex(this.track) + Number(b.dataset.step));
    }));
    onSettle(this.track, (i) => this.show(i));

    this.box = this.querySelector('dialog.bl-lightbox');
    if (this.box) {
      this.boxTrack = this.box.querySelector('.bl-lightbox__track');
      this.boxCurrent = this.box.querySelector('[data-current]');
      this.querySelectorAll('[data-zoom]').forEach((b) => b.addEventListener('click', () => this.open(Number(b.dataset.zoom), b)));
      this.box.querySelector('[data-close]').addEventListener('click', () => this.box.close());
      this.box.querySelectorAll('[data-lb-step]').forEach((b) => b.addEventListener('click', () => {
        goTo(this.boxTrack, trackIndex(this.boxTrack) + Number(b.dataset.lbStep));
      }));
      this.box.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          goTo(this.boxTrack, trackIndex(this.boxTrack) + (e.key === 'ArrowRight' ? 1 : -1));
        }
      });
      onSettle(this.boxTrack, (i) => { this.boxCurrent.textContent = String(i + 1); });
      this.box.addEventListener('close', () => {
        this.box.querySelectorAll('video').forEach((v) => v.pause());
        goTo(this.track, trackIndex(this.boxTrack));
        this.opener?.focus({ preventScroll: true });
        document.documentElement.classList.remove('bl-lock');
      });
    }
  }
  show(i) {
    if (this.current) this.current.textContent = String(i + 1);
    this.pickers.forEach((b) => {
      if (b.dataset.index === String(i)) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
    const thumb = this.querySelector(`.bl-gallery__thumbs button[data-index="${i}"]`);
    if (thumb && thumb.offsetParent) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  open(i, opener) {
    this.opener = opener;
    this.box.showModal();
    document.documentElement.classList.add('bl-lock');
    this.boxTrack.scrollTo({ left: i * this.boxTrack.clientWidth, behavior: 'instant' });
    this.boxCurrent.textContent = String(i + 1);
  }
}
if (!customElements.get('bl-gallery')) customElements.define('bl-gallery', BlGallery);

class BlStickyAtc extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.form);
    this.main = this.form?.querySelector('.bl-atc');
    this.footer = document.querySelector('.footer, footer');
    this.state = { above: false, footer: false };
    const update = () => {
      const show = this.state.above && !this.state.footer;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.querySelector('button').tabIndex = show ? 0 : -1;
    };
    // Measure on scroll (rAF-throttled). An IntersectionObserver misses jumps that skip past the button.
    let queued = false;
    this.measure = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.state.above = Boolean(this.main) && this.main.getBoundingClientRect().bottom < 0;
        this.state.footer = Boolean(this.footer) && this.footer.getBoundingClientRect().top < innerHeight;
        update();
      });
    };
    addEventListener('scroll', this.measure, { passive: true });
    addEventListener('resize', this.measure);
    this.measure();
    this.querySelector('button').addEventListener('click', (e) => {
      if (!this.form) return;
      this.form.requestSubmit(this.main || undefined);
    });
    document.addEventListener('bl:offer-change', (e) => {
      const label = this.querySelector('[data-bl-label]');
      if (label && e.detail.label) label.textContent = e.detail.label;
    });
  }
  disconnectedCallback() {
    removeEventListener('scroll', this.measure);
    removeEventListener('resize', this.measure);
  }
}
if (!customElements.get('bl-sticky-atc')) customElements.define('bl-sticky-atc', BlStickyAtc);

// Product anatomy: glowing dots on a photo. One card open at a time; Esc or a click outside closes it.
class BlHotspots extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[aria-controls]');
      if (btn && this.contains(btn)) {
        const id = btn.getAttribute('aria-controls');
        this.toggle(this.openId === id ? null : id, btn);
        return;
      }
      if (e.target.closest('[data-hot-close]')) this.toggle(null, this.lastDot);
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.openId) {
        e.stopPropagation();
        this.toggle(null, this.lastDot);
      }
    });
    this.outside = (e) => { if (this.openId && !this.contains(e.target)) this.toggle(null); };
    document.addEventListener('pointerdown', this.outside);
  }
  disconnectedCallback() { document.removeEventListener('pointerdown', this.outside); }
  toggle(id, from) {
    this.openId = id;
    this.querySelectorAll('.bl-hot__card').forEach((c) => {
      const open = c.id === id;
      c.hidden = !open;
      if (open) restartClass(c, 'is-pop');
    });
    this.querySelectorAll('[aria-controls]').forEach((b) => b.setAttribute('aria-expanded', String(b.getAttribute('aria-controls') === id)));
    if (id) {
      this.lastDot = this.querySelector(`.bl-hot__dot[aria-controls="${id}"]`);
      this.dataset.open = id;
    } else {
      delete this.dataset.open;
      if (from) from.focus({ preventScroll: true });
    }
  }
}
if (!customElements.get('bl-hotspots')) customElements.define('bl-hotspots', BlHotspots);

// Mode playground: 8 modes switch the glow animation, 4 levels set its strength.
class BlModePlay extends HTMLElement {
  connectedCallback() {
    this.glow = this.querySelector('.bl-glow');
    this.name = this.querySelector('[data-mode-name]');
    this.addEventListener('click', (e) => {
      const mode = e.target.closest('button[data-mode]');
      if (mode) {
        this.dataset.mode = mode.dataset.mode;
        this.glow.className = `bl-glow bl-glow--${mode.dataset.mode}`;
        if (this.name) this.name.textContent = mode.dataset.label || mode.dataset.mode;
        this.press('button[data-mode]', mode);
      }
      const level = e.target.closest('button[data-level]');
      if (level) {
        this.dataset.level = level.dataset.level;
        this.press('button[data-level]', level);
      }
    });
  }
  press(selector, active) {
    this.querySelectorAll(selector).forEach((b) => b.setAttribute('aria-pressed', String(b === active)));
  }
}
if (!customElements.get('bl-mode-play')) customElements.define('bl-mode-play', BlModePlay);
